import { z } from 'zod';

/**
 * The event types, mirroring `EventType` on the server. Forms need runtime values to build a picker
 * from, which the generated union type alone cannot provide.
 */
export const eventTypes = [
  'CarsAndCoffee',
  'Meet',
  'Concours',
  'Show',
  'DragNight',
  'TrackDay',
  'Autocross',
  'SwapMeet',
  'Other',
] as const;

/** An event type. */
export type EventType = (typeof eventTypes)[number];

/** Event types as a Zod enum. */
export const eventTypeSchema = z.enum(eventTypes);

/** Human-readable labels for each event type. */
export const eventTypeLabels: Record<EventType, string> = {
  CarsAndCoffee: 'Cars & Coffee',
  Meet: 'Meet',
  Concours: 'Concours',
  Show: 'Show',
  DragNight: 'Drag Night',
  TrackDay: 'Track Day',
  Autocross: 'Autocross',
  SwapMeet: 'Swap Meet',
  Other: 'Other',
};

/** The RSVP responses, mirroring `RsvpStatus` on the server. */
export const rsvpStatuses = ['Going', 'Interested'] as const;

/** An RSVP response. */
export type RsvpStatus = (typeof rsvpStatuses)[number];

/** Largest search radius accepted, matching the server. */
export const MAX_RADIUS_KM = 500;

/** Latitude in degrees. */
export const latitudeSchema = z
  .number({ error: 'Pick a location on the map.' })
  .min(-90, 'Latitude is out of range.')
  .max(90, 'Latitude is out of range.');

/** Longitude in degrees. */
export const longitudeSchema = z
  .number({ error: 'Pick a location on the map.' })
  .min(-180, 'Longitude is out of range.')
  .max(180, 'Longitude is out of range.');

/**
 * A local date-and-time as produced by a `datetime-local` input or a native picker.
 *
 * Held as the string the control gives back; the client converts it to an instant before it reaches
 * the API, which is what keeps a search correct across time zones.
 */
const localDateTimeSchema = z
  .string()
  .min(1, 'Enter a date and time.')
  .refine((value) => !Number.isNaN(Date.parse(value)), 'Enter a valid date and time.');

/**
 * An optional free-text field: blank input becomes `undefined` rather than an empty string, so
 * absent and deliberately-empty stay distinct.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();
}

/** Validates the submit-and-edit event form. */
export const eventFormSchema = z
  .object({
    title: z.string().trim().min(1, 'Enter a title.').max(200),
    description: optionalText(8000),
    type: eventTypeSchema,
    address: z.string().trim().min(1, 'Enter the address.').max(500),
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    startsAt: localDateTimeSchema,
    endsAt: z
      .string()
      .transform((value) => (value === '' ? undefined : value))
      .optional()
      .refine(
        (value) => value === undefined || !Number.isNaN(Date.parse(value)),
        'Enter a valid end date and time.',
      ),
  })
  .refine(
    (form) => form.endsAt === undefined || Date.parse(form.endsAt) >= Date.parse(form.startsAt),
    { message: 'The end must be after the start.', path: ['endsAt'] },
  );

/** The validated event form value. */
export type EventFormValues = z.infer<typeof eventFormSchema>;

/** Validates the discovery filters. */
export const eventFilterSchema = z.object({
  radiusKm: z
    .number({ error: 'Enter a radius.' })
    .min(1, 'Use at least 1 km.')
    .max(MAX_RADIUS_KM, `Use at most ${MAX_RADIUS_KM} km.`),
  types: z.array(eventTypeSchema),
  from: z.string().optional(),
  to: z.string().optional(),
});

/** The validated filter value. */
export type EventFilterValues = z.infer<typeof eventFilterSchema>;
